import React, { useCallback, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import Styles from './UsersTooltip.module.css';
import { SpeakerPersonIcon, Text, ProviderConsumer as FluentUIThemeConsumer, } from '@fluentui/react-northstar';
import { UserAvatar } from '../UserAvatar';
import { ThemeColorScheme } from '../../common/TeamsTheme';

interface UsersTooltipProps {
  ticketCollaborator: PlatormUserEntity[];
  isPluseShow?: boolean;
  position?: string;
  title?: string;
}

const POPUP_WIDTH = 180;
const POPUP_GAP = 8;

export const UsersTooltip = ({
  ticketCollaborator,
  isPluseShow,
  position,
  title
}: UsersTooltipProps) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const [show, setShow] = useState(false);
  const [popupStyle, setPopupStyle] = useState<{ top: number; left: number; height: number }>({ top: 0, left: 0, height: 0 });

  // Opens below the trigger for 'top' (kept for backward compatibility with existing
  // callers), otherwise above it. Positioned via a portal to document.body so the
  // popup is never clipped by a card's own overflow:hidden ancestors.
  const openBelow = position === 'top';
  const height = openBelow ? 150 : 120;

  const showPopup = useCallback(() => {
    clearTimeout(hideTimeoutRef.current);
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPopupStyle({
      top: openBelow ? rect.bottom + POPUP_GAP : rect.top - height - POPUP_GAP,
      left: Math.max(POPUP_GAP, Math.min(rect.left + rect.width / 2 - POPUP_WIDTH / 2, window.innerWidth - POPUP_WIDTH - POPUP_GAP)),
      height
    });
    setShow(true);
  }, [openBelow, height]);

  const scheduleHide = useCallback(() => {
    hideTimeoutRef.current = setTimeout(() => setShow(false), 100);
  }, []);

  return (
    <FluentUIThemeConsumer render={globalTheme => (
      <div
        ref={triggerRef}
        className={`${Styles.tooltip} inline-block cursor-pointer`}
        style={{ position: 'relative', ...ThemeColorScheme(globalTheme.siteVariables) }}
        onMouseEnter={showPopup}
        onMouseLeave={scheduleHide}
      >
        <Text size="small" className={`ml-1 cursor-pointer`} style={{ color: globalTheme.siteVariables.colorScheme.default.foreground }}>
          {isPluseShow && '+'}
          {ticketCollaborator?.length ?? 0}
        </Text>
        {show && ticketCollaborator?.length > 0 && ReactDOM.createPortal(
          <div
            className={Styles.tooltipcontent}
            style={{
              position: 'fixed',
              top: popupStyle.top,
              left: popupStyle.left,
              width: POPUP_WIDTH,
              height: popupStyle.height,
              zIndex: 9999999999999,
              ...ThemeColorScheme(globalTheme.siteVariables)
            }}
            onMouseEnter={showPopup}
            onMouseLeave={scheduleHide}
          >
            <div>
              <div className={`font-sans font-medium py-1.5 pl-2 ${Styles.borderBottom}`}>
                {title}
              </div>
              <div className={`flex flex-col ${openBelow ? Styles.tooltipInerConnentForTop : Styles.tooltipInerConnentForBottom} overflow-auto`}>
                {ticketCollaborator.map((user, index) => {
                  return (
                    <div key={index} className={`pl-2 py-1 ${index > 0 && Styles.borderTop}`}>
                      {(user?.AadObjectId || user?.UserName) ? (
                        <UserAvatar
                          {...{
                            idOrUpn: user?.AadObjectId ?? user?.UserName,
                            avatarProps: {
                              name: user?.FullName,
                              size: 'smaller',
                              className: 'cui-ml-2'
                            }
                          }}
                        />
                      ) : (
                        <SpeakerPersonIcon
                          size="large"
                          style={{ color: '#8f90a6' }}
                          variables={{ largeSize: '24px' }}
                        />
                      )}
                      &nbsp;
                      {!user?.FullName ? 'Unassigned' : user?.FullName}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>, document.body)}
      </div>
    )} />
  );
};
